import { Link, useParams } from 'react-router-dom';

// Public placeholder for /privacy and /terms. Replace the body strings
// before launch — these are intentionally short so they're obviously
// drafts. A lawyer should sign off on the real version.

const DOCS = {
  privacy: {
    title: 'Privacy Policy',
    updated: '2026-04-27',
    body: [
      'Relay Work, Inc. ("Relay Work") operates Alfred Voice, an AI phone-call platform. This policy explains what we collect, how we use it, and how you can reach us about it.',
      'WHAT WE COLLECT — When you sign up, we collect your name, work email, and the company information you provide. When Alfred Voice handles a call on your behalf, we collect the call audio, the transcribed conversation, the caller phone number, and the call duration. We collect product usage information (login times, dashboard activity) to operate the service and detect fraud.',
      'HOW WE USE IT — To provide the service: routing your calls, generating summaries, displaying transcripts, sending emails. To bill: through our payment processor, Stripe. To improve: aggregated, de-identified usage trends. We do NOT use your call content to train AI models that benefit other customers.',
      'SUBPROCESSORS — Anthropic (LLM), ElevenLabs (text-to-speech), Deepgram (speech-to-text), jambonz.cloud (telephony orchestration), Telnyx (PSTN), Resend (transactional email), Stripe (billing), AWS (infrastructure). We have data-processing terms with each.',
      'RETENTION — Call recordings and transcripts are retained for the duration of your subscription and for 30 days after cancellation, after which they are deleted. Account information is retained per legal record-keeping requirements.',
      'YOUR RIGHTS — You can request export or deletion of your data by emailing privacy@relaywork.co. We respond within 30 days.',
      'CONTACT — privacy@relaywork.co'
    ]
  },
  terms: {
    title: 'Terms of Service',
    updated: '2026-04-27',
    body: [
      'These Terms of Service govern your use of Alfred Voice. By signing up or using the service you agree to them.',
      'SERVICE — Alfred Voice provides AI-powered phone-call handling. We make commercially reasonable efforts to provide reliable service but do not guarantee uninterrupted availability.',
      'ACCEPTABLE USE — You agree not to use Alfred Voice for unlawful telemarketing (TCPA, robocalls without consent), harassment, fraud, or any purpose that violates applicable law in the jurisdictions where calls are placed or received.',
      'BILLING — Plans are month-to-month. You may cancel at any time with 30 days notice. Setup fees are refundable within 30 days. Overage minutes are billed at the rates shown in your dashboard. Failed payments suspend service after a 7-day grace period.',
      'CONTENT OWNERSHIP — You retain ownership of all call recordings, transcripts, and knowledge-base content you upload. We hold a limited license to process that content solely to deliver the service.',
      'LIABILITY — To the maximum extent permitted by law, our liability is limited to the fees you have paid in the trailing 12 months.',
      'CHANGES — We will notify you of material changes by email at least 14 days before they take effect. Continued use after the effective date constitutes acceptance.',
      'CONTACT — legal@relaywork.co'
    ]
  }
};

export default function Legal() {
  const { kind } = useParams();
  const doc = DOCS[kind];
  if (!doc) {
    return (
      <div className="min-h-screen bg-stone-950 text-stone-100 px-6 py-20 mx-auto max-w-3xl">
        <h1 className="text-3xl font-serif">Not found</h1>
        <p className="mt-3 text-stone-400">
          <Link to="/" className="text-alfred-gold hover:underline">← Home</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <header className="border-b border-stone-900">
        <div className="mx-auto max-w-3xl px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-alfred-gold font-serif text-2xl">Alfred</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Voice</span>
          </Link>
          <Link to="/login" className="text-sm text-stone-300 hover:text-alfred-gold">
            Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-14">
        <h1 className="text-4xl font-serif">{doc.title}</h1>
        <p className="mt-2 text-xs uppercase tracking-[0.2em] text-stone-500">
          Last updated {doc.updated}
        </p>
        <div className="mt-8 space-y-5 text-stone-300 leading-relaxed">
          {doc.body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
        <p className="mt-12 text-xs text-stone-500">
          This is a working draft pending counsel review. The version in effect at any
          given time is the one published at this URL.
        </p>
      </main>

      <footer className="border-t border-stone-900 mt-16">
        <div className="mx-auto max-w-3xl px-6 py-8 text-xs text-stone-500 flex items-center justify-between">
          <span>© Relay Work · Alfred Voice</span>
          <span className="space-x-4">
            <Link to="/faq" className="hover:text-alfred-gold">FAQ</Link>
            <Link to="/privacy" className="hover:text-alfred-gold">Privacy</Link>
            <Link to="/terms" className="hover:text-alfred-gold">Terms</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}
